import { HttpClient, HttpContext, HttpContextToken, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export const loadingDataRequest = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class BaseApiService {
  protected get baseUrl(): string {
    return '/api';
  }

  constructor(protected httpClient: HttpClient) {}

  getUrl(relativeUrl: string): string {
    return `${this.baseUrl}/${relativeUrl}`;
  }

  protected async get<T>(relativeUrl: string, params: any = null, contextSetter: (context: HttpContext) => void = null): Promise<T> {
    const context = contextSetter ? new HttpContext() : null;
    contextSetter?.(context);
    return await firstValueFrom(this.httpClient.get<T>(this.getUrl(relativeUrl), {
      params: this.createHttpParamsFromObject(params),
      context,
    }));
  }

  protected async getBlob(relativeUrl: string): Promise<Blob> {
    return await firstValueFrom(this.httpClient.get(this.getUrl(relativeUrl), { responseType: 'blob' }));
  }

  protected async post<TBody>(relativeUrl: string, body: TBody, contextSetter: (context: HttpContext) => void = null): Promise<void> {
    const context = contextSetter ? new HttpContext() : null;
    contextSetter?.(context);
    return await firstValueFrom(this.httpClient.post<void>(this.getUrl(relativeUrl), body, { context }));
  }

  protected async postWithResult<TBody, TResult>(relativeUrl: string, body: TBody, params: HttpParams = null, loadingData?: boolean): Promise<TResult> {
    const context = new HttpContext();
    if (loadingData === true) {
      context.set(loadingDataRequest, true);
    }
    return await firstValueFrom(this.httpClient.post<TResult>(this.getUrl(relativeUrl), body, { context, params }));
  }

  protected async put<T>(relativeUrl: string, body: T, params: any = null): Promise<void> {
    return await firstValueFrom(this.httpClient.put<void>(this.getUrl(relativeUrl), body, { params: this.createHttpParamsFromObject(params) }));
  }

  protected async delete(relativeUrl: string, params: any = null): Promise<void> {
    return await firstValueFrom(this.httpClient.delete<void>(this.getUrl(relativeUrl), { params: this.createHttpParamsFromObject(params) }));
  }

  protected async patch<TBody, TResult>(relativeUrl: string, body: TBody): Promise<TResult> {
    return await firstValueFrom(this.httpClient.patch<TResult>(this.getUrl(relativeUrl), body));
  }

  protected download(relativeUrl: string): void {
    const link = document.createElement('a');
    link.href = this.getUrl(relativeUrl);
    link.click();
  }

  private createHttpParamsFromObject<T>(obj: T): HttpParams {
    if (obj == null) {
      return null;
    }
    let params = new HttpParams();
    Object.entries(obj)
      .filter(([, value]) => value != null)
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => (params = params.append(key, v)));
        } else {
          params = params.append(key, value);
        }
      });
    return params;
  }

  protected getTenantParameter(tenantId: string): string {
    return tenantId != null ? `/${tenantId}` : '';
  }
}
